import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/useI18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { projectApi, logApi } from '../api/client';
import { getAvatarById } from '../utils/constants';

export default function Dashboard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [otherProjects, setOtherProjects] = useState([]);
  const [logs, setLogs] = useState([]);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [currentInvite, setCurrentInvite] = useState(null);
  const [invitesLoading, setInvitesLoading] = useState(false);

  useEffect(() => {
    if (!userLoading && !user) {
      return;
    }
    loadDashboard();
  }, [user, userLoading]);

  async function loadDashboard() {
    try {
      const res = await projectApi.getList();
      if (res.success) {        
        const projectsList = res.data.projects;
        console.log("当前拉取的工程列表",projectsList);
        const currentUserId = res.data.current_user_id;

        if (projectsList.length === 0) {
          setProjects([]);
          setLoading(false);
          return;
        }

        // 默认选中第一个或 URL 参数指定的项目
        const urlParams = new URLSearchParams(window.location.search);
        const currentId = urlParams.get('pid') || projectsList[0].project_id;
        const active = projectsList.find(p => p.project_id == currentId) || projectsList[0];

        setProjects(projectsList);
        setActiveProject(active);
        setOtherProjects(projectsList.filter(p => p.project_id != active.project_id));
        setIsSupervisor(active.supervisor_id == currentUserId);

        // 加载日志
        await loadLogs(active.project_id, active.type);
      }
    } catch (err) {
      console.error(t('loading') + ' ' + t('error'), err);
    } finally {
      setLoading(false);
    }
  }

  // 加载邀请列表
  useEffect(() => {
    if (user) {
      loadInvites();
    }
  }, [user]);

  async function loadInvites() {
    console.log("【读取邀请】loadInvites");
    setInvitesLoading(true);
    try {
      const res = await projectApi.getInvites();
      if (res.success) {
        const invitesList = res.data.invites;
        console.log("【邀请列表】",invitesList);
        setInvites(invitesList);
        // 只显示第一个邀请
        if (invitesList.length > 0) {
          setCurrentInvite(invitesList[0]);
        } else {
          setCurrentInvite(null);
        }
      }
    } catch (err) {
      console.error(t('loading') + ' ' + t('error'), err);
    } finally {
      setInvitesLoading(false);
    }
  }

  async function handleInviteResponse(projectId, action) {
    try {
      const res = await projectApi.respondInvite(projectId, action);
      if (res.success) {
        alert(res.message);
        // 移除已处理的邀请，显示下一个
        const remainingInvites = invites.filter(inv => inv.project_id !== projectId);
        setInvites(remainingInvites);
        if (remainingInvites.length > 0) {
          setCurrentInvite(remainingInvites[0]);
        } else {
          setCurrentInvite(null);
        }
        // 刷新工程列表
        await loadDashboard();
      } else {
        alert(res.message || t('actionFailed'));
      }
    } catch (err) {
      alert(t('actionFailed'));
    }
  }

  async function loadLogs(projectId, projectType) {
    try {
      const res = await projectApi.getLogs(projectId);
      if (res.success) {
        setLogs(res.data.logs);
      }
    } catch (err) {
      console.error(t('loading') + ' ' + t('recentChanges') + ' ' + t('error'), err);
    }
  }

  async function handleProjectChange(project) {
    setActiveProject(project);
    setOtherProjects(projects.filter(p => p.project_id != project.project_id));
    setIsSupervisor(project.supervisor_id == user?.id);
    await loadLogs(project.project_id, project.type);
    // 更新 URL 参数
    navigate(`?pid=${project.project_id}`, { replace: true });
  }

  async function handleAction(apiFunc, logId, isDelete = false) {
    const msg = isDelete ? t('confirmDelete') : t('confirmAction');
    if (!confirm(msg)) return;

    try {
      const res = await apiFunc(logId);
      if (res.success) {
        loadDashboard();
      } else {
        alert(res.message || t('actionFailed'));
      }
    } catch (e) {
      alert(t('actionFailed'));
    }
  }

  if (userLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400">{t('loading')}</div>
      </div>
    );
  }

  if (!activeProject) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar user={user} showAddButton={false} />
        <main className="container mx-auto p-4 max-w-xl space-y-6">
          {/* 待确认的邀请卡片 - 无工程时也显示 */}
          {currentInvite && (
            <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm p-4 border border-amber-200">
              <h3 className="text-xs font-bold text-amber-600 mb-3 flex items-center">
                <span className="w-1.5 h-4 bg-amber-500 rounded-full mr-2"></span>
                {t('pendingInvites') || '待确认的邀请'}
                {invites.length > 1 && <span className="ml-2 text-[10px] text-amber-400">({`还有 ${invites.length - 1} 个`})</span>}
              </h3>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-sm text-gray-800">
                      <span className="font-bold">{currentInvite.supervisor_name}</span>
                      {t('inviteYouToProject') || ' 邀请你执行工程 '}
                      <span className="font-bold text-indigo-600">{currentInvite.title}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      onClick={() => handleInviteResponse(currentInvite.project_id, 'accept')}
                      className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-all"
                      title={t('accept') || '接受'}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => handleInviteResponse(currentInvite.project_id, 'reject')}
                      className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-all"
                      title={t('reject') || '拒绝'}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-6xl mb-4">🏜️</div>
            <h2 className="text-xl font-bold text-gray-800">{t('noProject')}</h2>
            <p className="text-gray-400 text-xs mt-2 mb-8">{t('noProjectHint')}</p>
            <Link to="/create" className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-bold shadow-lg active:scale-95 transition-all">
              🚀 {t('createProject')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const isNegativeSystem = activeProject.type == 1;
  const pointsLabel = isNegativeSystem ? t('pendingPoints') : t('currentPoints');
  const pointsColor = isNegativeSystem ? "text-red-600" : "text-green-600";

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar user={user} onAddClick={() => navigate(`/add?pid=${activeProject.project_id}`)} showAddButton={true} />

      <main className="container mx-auto p-4 max-w-xl space-y-6">

        {/* 待确认的邀请卡片 - 只显示一个 */}
        {currentInvite && (
          <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm p-4 border border-amber-200">
            <h3 className="text-xs font-bold text-amber-600 mb-3 flex items-center">
              <span className="w-1.5 h-4 bg-amber-500 rounded-full mr-2"></span>
              {t('pendingInvites') || '待确认的邀请'}
              {invites.length > 1 && <span className="ml-2 text-[10px] text-amber-400">({`还有 ${invites.length - 1} 个`})</span>}
            </h3>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-amber-100">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-sm text-gray-800">
                    <span className="font-bold">{currentInvite.supervisor_name}</span>
                    {t('inviteYouToProject') || ' 邀请你执行工程 '}
                    <span className="font-bold text-indigo-600">{currentInvite.title}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <button
                    onClick={() => handleInviteResponse(currentInvite.project_id, 'accept')}
                    className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-all"
                    title={t('accept') || '接受'}
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => handleInviteResponse(currentInvite.project_id, 'reject')}
                    className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-all"
                    title={t('reject') || '拒绝'}
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 当前工程卡片 */}
        <section className="bg-white rounded-2xl shadow-sm p-6 active-project-card relative">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{activeProject.title}</h2>
              <p className="text-gray-400 text-[10px] mt-1 uppercase tracking-wider italic">
                {t('identity')}：{isSupervisor ? `${t('supervisor')} (${t('performer')}：${activeProject.performer_name})` : `${t('performer')} (${t('supervisor')}：${activeProject.supervisor_name})`}
              </p>
            </div>
            {isSupervisor && (
              <button
                onClick={() => navigate(`/edit?pid=${activeProject.project_id}`)}
                className="text-indigo-500 p-2 hover:bg-indigo-50 rounded-full transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </button>
            )}
          </div>
          <div className="text-center py-4">
            <p className="text-[10px] text-gray-400 font-bold uppercase mb-1">{pointsLabel}</p>
            <h2 className={`text-5xl font-black ${pointsColor} tracking-tighter`}>{activeProject.current_points}</h2>
          </div>
          
          {/* 工程统计按钮 */}
          <div className="absolute bottom-4 right-4">
            <button
              onClick={() => {
                if (user?.is_vip) {
                  navigate(`/stats?pid=${activeProject.project_id}`);
                } else {
                  alert(t('vipOnlyFeature') || '此为VIP用户高级功能');
                }
              }}
              className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-indigo-500 transition-colors bg-gray-50 hover:bg-indigo-50 px-2 py-1 rounded-full"
              title={t('projectStats')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
              {t('projectStats')}
            </button>
          </div>
        </section>

        {/* 最近变动明细 */}
        <div className="space-y-3">
          <h3 className="font-bold text-gray-700 text-xs flex items-center px-2">
            <span className="w-1.5 h-4 bg-indigo-500 rounded-full mr-2"></span> {t('recentChanges')}
          </h3>
          <div id="log_list" className="space-y-3">
            {logs.map(log => {
              const colorClass = isNegativeSystem
                ? (log.change_value >= 0 ? 'text-red-500' : 'text-green-500')
                : (log.change_value >= 0 ? 'text-green-500' : 'text-red-500');

              const statusBadge = log.status == 0
                ? `<span class="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[9px] rounded-full font-bold">${t('pending')}</span>`
                : log.status == 2
                ? `<span class="px-2 py-0.5 bg-red-100 text-red-700 text-[9px] rounded-full font-bold">${t('rejected')}</span>`
                : '';

              return (
                <div key={log.log_id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-800 text-sm">{log.reason}</span>
                      {log.status == 0 && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[9px] rounded-full font-bold">{t('pending')}</span>}
                      {log.status == 2 && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[9px] rounded-full font-bold">{t('rejected')}</span>}
                    </div>
                    <p className="text-gray-400 text-[10px] mt-1">{log.business_time} · {log.comment || t('noComment')}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-black ${colorClass} mb-1`}>
                      {log.change_value >= 0 ? '+' : ''}{log.change_value}
                    </div>
                    {log.status == 1
                      ? <p className="text-[10px] text-gray-400 font-mono mt-0.5">{t('balance')} {log.balance_after}</p>
                      : <p className="text-[9px] text-yellow-500 italic mt-0.5">{t('pendingStatus')}</p>
                    }
                    <div className="flex gap-1 justify-end mt-1">
                      {isSupervisor && log.status == 0 && (
                        <>
                          <button
                            onClick={() => handleAction(logApi.confirm, log.log_id)}
                            className="px-2 py-1 bg-green-600 text-white text-[9px] rounded-md font-bold"
                          >
                            {t('confirm')}
                          </button>
                          <button
                            onClick={() => handleAction(logApi.reject, log.log_id)}
                            className="px-2 py-1 bg-red-500 text-white text-[9px] rounded-md font-bold"
                          >
                            {t('reject')}
                          </button>
                        </>
                      )}
                      {log.user_id == user?.id && log.status != 1 && (
                        <button
                          onClick={() => handleAction(logApi.withdraw, log.log_id, true)}
                          className="px-2 py-1 border border-gray-200 text-gray-400 text-[9px] rounded-md font-bold"
                        >
                          {t('withdraw')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {logs.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-xs italic">{t('noRecords')}</div>
            )}
          </div>
        </div>

        {/* 其他工程 */}
        <div className="pt-4 space-y-3">
          <h3 className="font-bold text-gray-400 text-[10px] uppercase tracking-widest px-2 flex justify-between">
            <span>{t('switchProject')}</span>
            <span id="other_count">{otherProjects.length}</span>
          </h3>
          <div id="other_projects_list" className="space-y-2">
            {otherProjects.map(p => {
              const isNeg = p.type == 1;
              const points = parseFloat(p.current_points);
              const colorClass = isNeg
                ? (points >= 0 ? 'text-red-400' : 'text-green-400')
                : (points >= 0 ? 'text-green-400' : 'text-red-400');

              return (
                <div
                  key={p.project_id}
                  onClick={() => handleProjectChange(p)}
                  className="project-item bg-white p-3 rounded-xl border border-gray-100 shadow-sm flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-all"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <span className={`w-2 h-2 rounded-full ${isNeg ? 'bg-red-400' : 'bg-green-400'} mr-3 shrink-0`}></span>
                    <div className="truncate">
                      <span className="text-sm font-medium text-gray-600">{p.title}</span>
                      <span className={`ml-1 text-[8px] px-1 py-0.5 rounded ${isNeg ? 'bg-red-50 text-red-400' : 'bg-green-50 text-green-400'} font-bold`}>
                        {isNeg ? t('negativeMode') : t('positiveMode')}
                      </span>
                    </div>
                  </div>
                  <span className={`text-xs font-bold ${colorClass} ml-2`}>
                    {p.current_points}
                  </span>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => navigate('/create')}
            className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 font-bold text-sm hover:border-indigo-300 hover:text-indigo-500 transition-all flex items-center justify-center"
          >
            <span className="text-lg mr-2">➕</span> {t('newProject')}
          </button>
        </div>
      </main>
    </div>
  );
}

function NavBar({ user, onAddClick, showAddButton = true }) {
  const avatar = getAvatarById(user?.avatar_id);
  const navigate = useNavigate();
  const { t } = useI18n();

  return (
    <nav className="bg-indigo-600 text-white p-4 shadow-lg sticky top-0 z-50">
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/profile')}>
          <span id="nav_avatar" className="text-xl">{avatar}</span>
          <div className="flex flex-col">
            <h1 className="text-sm font-bold leading-none">{t('loginTitle')}</h1>
            <span id="nav_user_nickname" className="text-[10px] text-indigo-200 mt-1 font-normal italic">{t('profile')}：{user?.nickname}</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <LanguageSwitcher />
          {showAddButton && (
            <button
              id="header_add_btn"
              onClick={onAddClick}
              className="bg-white text-indigo-600 px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm active:scale-95 transition-all"
            >
              ➕ {t('apply')}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
